import { useEffect, useState } from "react";

import { createDailyPuzzleSource, type DailyPuzzleResult } from "../puzzle";

const source = createDailyPuzzleSource();

/**
 * React hook that fetches the daily puzzle through the configured
 * {@link DailyPuzzleSource} (api or local — see
 * `VITE_COGQUEST_PUZZLE_SOURCE`).
 *
 * Returns `undefined` while the first fetch is in-flight; afterwards
 * returns the full {@link DailyPuzzleResult}, so the caller can render
 * the `ok` / `pending` / `unavailable` states distinctly and surface
 * the stale-cache flag.
 *
 * Roll-over support across UTC midnight is intentionally out of scope —
 * a player who leaves the tab open will see whatever was fetched at
 * mount until they reload.
 */
export function useTodaysPuzzle(): DailyPuzzleResult | undefined {
  const [result, setResult] = useState<DailyPuzzleResult | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;
    source.fetchTodaysPuzzle().then(
      (r) => {
        if (!cancelled) setResult(r);
      },
      (err: unknown) => {
        // Unexpected error from the source (network failure, malformed
        // response, etc.) — surface as "unavailable" so the UI can
        // render a service-problem state rather than spinning forever.
        // `retryAfterSeconds` is unknown here.
        console.error(err);
        if (!cancelled) {
          setResult({ status: "unavailable", retryAfterSeconds: undefined });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, []);

  return result;
}
